/* eslint-disable camelcase */
import { useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useUI from 'hooks/use-ui';
import { Session } from 'utils/session';
import { AdPlacement } from '../placements';

const fetchedTrackers = new Set<string>();

type FetchTrackerOptions = {
  uiPlacement: AdPlacement;
  uiPlacementIndex: number;
};

type FetchTracker = {
  (url: string, options: FetchTrackerOptions, retries?: number, backoff?: number): Promise<void>;
};

type UIMeta = {
  ui_variant: string;
  ui_placement: AdPlacement;
  ui_placement_index: number;
};

function encodeUrlSafeBase64Params(params: Record<string, any>): string {
  return btoa(JSON.stringify(params)).replace(/\+/g, '-').replace(/\//g, '_');
}

function encodeMetaQueryParams(urlStr: string, meta: UIMeta): URL {
  const url = new URL(urlStr.startsWith('http') ? urlStr : window.location.origin + urlStr);

  url.searchParams.append('ui_meta', encodeUrlSafeBase64Params(meta));
  return url;
}

export function useFetchTracker(): FetchTracker {
  const flags = useFlags();
  const UI = useUI();

  const isEnabled = flags['growth.ads.tracking.enabled'] ?? true;

  return useCallback<FetchTracker>(
    async function fetchWithRetry(url, options, retries = 3, backoff = 300) {
      if (!isEnabled) {
        return;
      }

      if (fetchedTrackers.has(url)) {
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const meta: UIMeta = {
        ui_variant: UI.analyticsMenuVariant,
        ui_placement: options.uiPlacement,
        ui_placement_index: options.uiPlacementIndex,
      };

      try {
        const response = await fetch(encodeMetaQueryParams(url, meta), {
          signal: controller.signal,
          headers: {
            ...Session.getInstance().header,
            'x-dutchie-menu': UI.analyticsMenuVariant,
            'x-dutchie-version': process.env.NEXT_PUBLIC_CURRENT_TAG ?? '',
          },
        });

        if (response.ok) {
          fetchedTrackers.add(url);
          return;
        }

        if (response.status >= 500 && response.status < 600) {
          throw new Error(`Server Error: ${response.status}`);
        }
      } catch (e) {
        if (retries > 1) {
          await new Promise((res) => setTimeout(res, backoff));
          await fetchWithRetry(url, options, retries - 1, backoff * 2);
        }

        console.error('useFetchTracker failed', e);
      } finally {
        clearTimeout(timeoutId);
      }
    },
    [UI.analyticsMenuVariant, isEnabled]
  );
}
